import { useState } from "react";
import { toast } from "sonner";
import { Clock, Video, MapPin, ScrollText } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";
import { Reveal, MaskedLine } from "../components/Reveal";

const INITIAL_FORM = {
  nome: "", email: "", telefone: "",
  tipo_entidade: "Particular",
  tipo_consulta: "Consulta Inicial (60 min)",
  formato: "Online (videochamada)",
  data_preferencial: "", assunto: "", mensagem: "",
};

export default function BookingPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState(INITIAL_FORM);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/bookings", form);
      toast.success(t("booking.toastSuccess"), {
        description: t("booking.toastSuccessDesc"),
      });
      setForm(INITIAL_FORM);
    } catch (err) {
      toast.error(t("booking.toastError"), {
        description: formatApiErrorDetail(err.response?.data?.detail),
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="booking-page" className="pt-32 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="overline-label mb-6">{t("booking.overline")}</p>
          <h1 className="text-4xl sm:text-5xl font-serif font-medium tracking-tight text-ebony" data-testid="booking-title">
            <MaskedLine delay={0.1}>{t("booking.title1")}</MaskedLine>
            <MaskedLine delay={0.25}><em className="italic text-forest">{t("booking.title2")}</em></MaskedLine>
          </h1>
          <p className="text-base text-[#4D5248] leading-relaxed mt-8 max-w-md">
            {t("booking.intro")}
          </p>

          <div className="mt-12 space-y-px border border-[#E2E4DC] bg-[#E2E4DC]">
            {t("booking.types").map((c, i) => (
              <Reveal key={c.title} delay={0.06 * i}>
                <div className="bg-alabaster p-6" data-testid={`consult-type-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-serif text-lg font-medium text-ebony">{c.title}</h3>
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                      <Clock size={12} /> {c.duration}
                    </span>
                  </div>
                  <p className="text-sm text-[#757A70] leading-relaxed">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 space-y-4 text-sm text-[#4D5248]">
            <p className="flex items-center gap-3"><Video size={15} className="text-olive" /> {t("booking.formatOnline")}</p>
            <p className="flex items-center gap-3"><MapPin size={15} className="text-olive" /> {t("booking.formatInPerson")}</p>
            <p className="flex items-start gap-3 text-xs text-[#757A70] leading-relaxed pt-4 border-t border-[#E2E4DC]">
              <ScrollText size={15} className="text-gold shrink-0 mt-0.5" />
              {t("booking.deont")}
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            <form
              onSubmit={submit}
              data-testid="booking-form"
              className="bg-white border border-[#E2E4DC] p-8 sm:p-12"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold mb-10">
                {t("booking.formTitle")}
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-nome">{t("booking.labelName")}</label>
                  <input id="bk-nome" data-testid="booking-name-input" required value={form.nome} onChange={set("nome")} className="input-field" placeholder={t("booking.phName")} />
                </div>
                <div>
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-email">{t("booking.labelEmail")}</label>
                  <input id="bk-email" data-testid="booking-email-input" required type="email" value={form.email} onChange={set("email")} className="input-field" placeholder={t("booking.phEmail")} />
                </div>
                <div>
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-tel">{t("booking.labelPhone")}</label>
                  <input id="bk-tel" data-testid="booking-phone-input" value={form.telefone} onChange={set("telefone")} className="input-field" placeholder={t("booking.phPhone")} />
                </div>
                <div>
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-ent">{t("booking.labelEntity")}</label>
                  <select id="bk-ent" data-testid="booking-entity-select" value={form.tipo_entidade} onChange={set("tipo_entidade")} className="input-field">
                    {t("booking.entities").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-tipo">{t("booking.labelType")}</label>
                  <select id="bk-tipo" data-testid="booking-type-select" value={form.tipo_consulta} onChange={set("tipo_consulta")} className="input-field">
                    {t("booking.consultOptions").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-formato">{t("booking.labelFormat")}</label>
                  <select id="bk-formato" data-testid="booking-format-select" value={form.formato} onChange={set("formato")} className="input-field">
                    {t("booking.formatOptions").map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-data">{t("booking.labelDate")}</label>
                  <input id="bk-data" data-testid="booking-date-input" type="datetime-local" value={form.data_preferencial} onChange={set("data_preferencial")} className="input-field" />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-assunto">{t("booking.labelSubject")}</label>
                  <input id="bk-assunto" data-testid="booking-subject-input" required value={form.assunto} onChange={set("assunto")} className="input-field" placeholder={t("booking.phSubject")} />
                </div>
                <div className="sm:col-span-2">
                  <label className="overline-label block mb-2 !text-[10px]" htmlFor="bk-msg">{t("booking.labelMessage")}</label>
                  <textarea id="bk-msg" data-testid="booking-message-input" rows={5} value={form.mensagem} onChange={set("mensagem")} className="input-field resize-none" placeholder={t("booking.phMessage")} />
                </div>
              </div>

              <button
                type="submit"
                data-testid="booking-form-submit-button"
                disabled={sending}
                className="btn-primary w-full mt-10 disabled:opacity-60"
              >
                {sending ? t("booking.sending") : t("booking.submit")}
              </button>
              <p className="text-[11px] text-[#9AA096] leading-relaxed mt-5 text-center">
                {t("booking.disclaimer")}
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
